<template>
  <BaseInputSelect
    v-model="time"
    :emit-value="emitValue"
    :new-design="newDesign"
    option-label="label"
    option-prop="value"
    :options="filteredTimeOptions"
    use-input
    @filter="filterOptions"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import BaseInputSelect from "shared/components/base/BaseInputSelect";
import { getDurationFromTime } from "shared/helpers/date";

interface Props {
  emitValue?: boolean;
  interval?: number;
  military?: boolean;
  minTime?: number | string;
  newDesign?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  minTime: 0,
  interval: 0,
});

const time = defineModel<string>({ default: "" });

const inputValue = ref("");

function isDisabled(optionTime: string | number): boolean {
  if (!props.minTime) return false;

  const displayTime: number = props.military
    ? getDurationFromTime(optionTime as string)
    : (optionTime as number) / 1000;

  const minTime: number = props.military
    ? getDurationFromTime(props.minTime as string)
    : (props.minTime as number);

  if (displayTime < minTime) {
    return true;
  }

  if (
    props.interval &&
    (displayTime - minTime) % (props.interval / 1000) !== 0
  ) {
    return true;
  }

  return false;
}

const timeOptions = computed(() => {
  const options = [];

  for (let hourIndex = 0; hourIndex < 24; hourIndex += 1) {
    for (let index = 0; index < 4; index += 1) {
      let hours: number | string = hourIndex % 12;
      hours = hours === 0 ? 12 : hours;
      hours = hours < 10 ? `0${hours}` : hours;

      let hours24: number | string = hourIndex;
      if (hours24 < 10) hours24 = `0${hours24}`;

      let minutes: number | string = index * 15;
      minutes = index === 0 ? "00" : minutes;
      const ampm = hourIndex >= 12 ? "PM" : "AM";

      const time24 = `${hours24}:${minutes}`;

      const label = props.military ? time24 : `${hours}:${minutes} ${ampm}`;

      const timeValue = hourIndex * 1000 * 3600 + index * 15 * 60 * 1000;

      const value = props.military ? time24 : timeValue;

      const disabled = isDisabled(value);

      options.push({
        label,
        value,
        disabled,
        time: timeValue,
      });
    }
  }

  return options;
});

const filteredTimeOptions = computed(() =>
  timeOptions.value.filter((option) => {
    if (!inputValue.value) return true;

    return option.label.includes(inputValue.value);
  })
);

function filterOptions(value: string) {
  inputValue.value = value;
}
</script>
